import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { Box } from "@mui/material";
import { reportAssets } from "e.sic-platform.webclient/src/components/inspection/reportAssets";
import VideoAnalysisDiagram from "e.sic-platform.webclient/src/pages/analysis/components/video-analysis-diagram/VideoAnalysisDiagram";
import DamagesVideoAnalysis from "e.sic-platform.webclient/src/pages/analysis/components/DamagesVideoAnalysis";
import Tools from "e.sic-platform.webclient/src/pages/analysis/components/video-analysis-diagram/tools/Tools";
import {  DamagesStorageModel } from "e.sic-platform.webclient/src/services/damage/DamagesStorage";
import { AnalysisConfidenceStorageModel } from "e.sic-platform.webclient/src/services/organization-analysis-confidence/AnalysisConfidenceStorage";
import { featureFlagsStorage } from "e.sic-platform.webclient/src/services/feature-flags/FeatureFlagsStorage";
import { Flags } from "e.sic-platform.webclient/src/generated/FeatureFlags";
import { AnalysisService } from "e.sic-platform.webclient/src/services/analysis/AnalysisService";
import { api } from "e.sic-platform.webclient/src/services/api/ApiService";


const emptyDamagesStorage = DamagesStorageModel.create({ inspectionId: 0 });
emptyDamagesStorage.mapResponse({ totalCount: 0, rows: [], header: [] });
interface IVideoAnalysisDiagramSectionProps {
  videoAnalysis: AnalysisService;
}

export default observer(function VideoAnalysisDiagramSection(props: IVideoAnalysisDiagramSectionProps) {
    const { videoAnalysis } = props;
    const [confidenceStorage] = useState(() =>
      AnalysisConfidenceStorageModel.create({ autoLoad: false }, { api })
    );
    const captureRef = useRef<HTMLDivElement>(null);

    // Cache ONLY the chart/plot (not the Graph tools panel) as an image once it
    // has rendered, so the verified HTML report — exported from the Comparison
    // tab where this is unmounted — can embed a full-width graph. Best-effort;
    // failures just leave the report without the graph.
    useEffect(() => {
      if (!videoAnalysis.analysisSuccessful) return;
      const el = captureRef.current;
      if (!el) return;
      const id = window.setTimeout(() => {
        // .catch, not `void`: `void` discards the value, not a rejection, so anything that
        // escaped the capture surfaced as an unhandled rejection -- the red dev overlay on a
        // background job the reviewer never asked for.
        reportAssets.captureAnalysisGraph(el).catch((error) => {
          console.warn("Analysis graph capture failed; the report will omit it.", error);
        });
      }, 1500);
      return () => window.clearTimeout(id);
    }, [videoAnalysis.analysisSuccessful]);

    return (
        <Box
          sx={{
            display: "flex",
            gap: 2,
            mt: 4,
            width: "100%",
            alignItems: "stretch",
            flexDirection: { xs: "column", md: "row" },
          }}
        >
        {/* Plot — same height as the tools panel (stretch); the card fills its column.
            The capture ref is on THIS box only so the HTML report embeds just the
            chart (no Graph tools panel). */}
        <Box
          ref={captureRef}
          sx={{
            flex: { md: 2 },
            minWidth: 0,
            display: "flex",
            "& > *": { width: "100%", display: "flex", flexDirection: "column" },
            "& .va-diagram-container": { flex: 1, width: "100%" },
            "& .va-graph-container": { flex: 1, display: "flex", flexDirection: "column" },
            // Grow the chart area so the plot card fills to the same bottom y as
            // the tools card.
            "& .va-plot-container": { flex: 1 },
          }}
        >
          {featureFlagsStorage.isFeatureEnabled(
            Flags.Render_VA_Diagram_In_Platform
          ) && (
            <VideoAnalysisDiagram
              video={videoAnalysis?.video}
              damagesStorage={emptyDamagesStorage}
              videoAnalysis={videoAnalysis!}
              onVAFinished={videoAnalysis.onServiceFinished}
              readonly
              canAnalyze={true}
            />
          )}
          {!featureFlagsStorage.isFeatureEnabled(
            Flags.Render_VA_Diagram_In_Platform
          ) && (
            <DamagesVideoAnalysis
              video={videoAnalysis?.video}
              damagesStorage={emptyDamagesStorage}
              videoAnalysis={videoAnalysis!}
              onVAFinished={videoAnalysis.onServiceFinished}
              inspectionLocked={false}
            />
          )}
          {videoAnalysis.analysisFailed && <div style={{marginBottom: "400px"}}></div>}
        </Box>

        <Box
          sx={{
            flex: { md: 1 },
            minWidth: 0,
            display: "flex",
            // Put "Graph tools" INSIDE the bordered square: move the border/shadow
            // onto the whole tools-container and strip it from the inner card.
            "& .legends-container": { width: "100%", height: "100%" },
            "& .tools-container": {
              flex: 1,
              width: "100%",
              border: "1px solid rgba(0,0,0,0.15)",
              boxShadow: "0 5px 10px 0 rgba(0,0,0,0.15)",
              borderRadius: 1,
              p: 2,
              gap: 1.5,
            },
            "& .tools-label": { fontWeight: 600 },
            "& .tools-content-container": {
              flex: 1,
              width: "100%",
              minWidth: 0,
              minHeight: "auto",
              border: "none !important",
              boxShadow: "none !important",
              borderRadius: 0,
              px: 0,
            },
          }}
        >
          <div className="legends-container">
            {featureFlagsStorage.isFeatureEnabled(
              Flags.Render_VA_Diagram_In_Platform
            ) && (
              <Tools
                videoAnalysisService={videoAnalysis!}
                analysisConfidenceStorage={confidenceStorage}
              />
            )}
          </div>
        </Box>
        </Box>
    );
});

